import TableCell from '@material-ui/core/TableCell'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { AgencySettings_EnabledFeaturesEnum, PolicyRule_VehicleTypesEnum } from 'src/api/gen/api'
import { IPolicyRuleBeginnerAllowedHours } from 'src/api/types/policy'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { SimpleTable } from 'src/components/Table'
import { useAgencySettings, useCheckEnabledFeature } from 'src/layout/AgencySettings'

import { FieldTitle } from '../../commons'
import { GeographiesField, RuleNameField, VehicleCategoriesField } from '../fields'

type Row = [
  TRulesValidation,
  string, // name,
  string, // ruleId
  PolicyRule_VehicleTypesEnum[], // vehicleTypes
  string[], // geographies
  string[] // buffered_geographies
]

function BeginnerAllowedHoursRule(props: IBaseTableProps<IPolicyRuleBeginnerAllowedHours>) {
  const { t } = useTranslation()
  const agencySettings = useAgencySettings()
  const buffered_areas_enabled = useCheckEnabledFeature()(AgencySettings_EnabledFeaturesEnum.buffered_areas)

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleBeginnerAllowedHours) =>
    (val: string | string[] | number | PolicyRule_VehicleTypesEnum[] | null): void => {
      const newRules: IPolicyRuleBeginnerAllowedHours[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        if (key === 'vehicle_types') {
          // Kind of a hack here again linked with the "vehicle_category" dev
          let vehicleTypes: any[] = []
          for (const vehicleType in agencySettings.device_type_to_category_mapping as any) {
            if ((val as string[]).includes((agencySettings.device_type_to_category_mapping as any)[vehicleType])) {
              vehicleTypes = [...vehicleTypes, vehicleType]
            }
          }
          return { ...rule, [key]: vehicleTypes as PolicyRule_VehicleTypesEnum[] }
        }
        return { ...rule, [key]: val }
      })
      props.handleChange(newRules)
    }

  const HEADERS: [string, string?][] = [
    [t('rule.name'), undefined],
    [t('rule.vehicle_categories'), t('rule.vehicle_categories.tooltip')],
    [t('rule.geographies'), undefined],
  ]

  if (buffered_areas_enabled) {
    HEADERS.push([t('rule.buffered_geographies'), t('rule.buffered_geographies.tooltip')])
  }

  const items = props.rules.map(
    (rule: IPolicyRuleBeginnerAllowedHours, idx): Row => [
      props.validation[idx],
      rule.name,
      rule.rule_id,
      rule.vehicle_types,
      rule.geographies,
      rule.buffered_geographies,
    ]
  )

  return (
    <SimpleTable
      headerRow={HEADERS.map(([title, tooltip]) => (
        <TableCell key={`key-${title}`}>
          <FieldTitle title={title} tooltip={tooltip} />
        </TableCell>
      ))}
      items={items}
      rowRenderer={([ruleValidation, name, ruleId, vehicleTypes, geographies, buffered_geographies]: Row) => (
        <>
          <TableCell style={{ height: '100px', verticalAlign: 'bottom' }}>
            <RuleNameField
              value={name}
              handleChange={handleChangeRule(ruleId, 'name')}
              disabled={props.disabled}
              error={!ruleValidation.isFieldValid('name')}
              helperText={ruleValidation.getFieldErrorMsg('name')}
            />
          </TableCell>

          <TableCell style={{ height: '100px', verticalAlign: 'bottom' }}>
            <VehicleCategoriesField
              value={vehicleTypes}
              handleChange={handleChangeRule(ruleId, 'vehicle_types')}
              disabled={props.disabled}
              error={!ruleValidation.isFieldValid('vehicle_types')}
              helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
            />
          </TableCell>

          <TableCell style={{ maxWidth: '400px' }}>
            <GeographiesField
              value={geographies}
              allGeographies={props.allGeographies}
              handleChange={handleChangeRule(ruleId, 'geographies')}
              disabled={props.disabled}
              error={!ruleValidation.isFieldValid('geographies')}
              helperText={ruleValidation.getFieldErrorMsg('geographies')}
            />
          </TableCell>

          {buffered_areas_enabled && (
            <TableCell
              align="justify"
              style={{
                maxWidth: '400px',
                height: '100px',
                verticalAlign: 'bottom',
              }}
            >
              <GeographiesField
                value={buffered_geographies}
                allGeographies={props.allGeographies}
                handleChange={handleChangeRule(ruleId, 'buffered_geographies')}
                disabled={props.disabled}
                helperText={t('rule.buffered_geographies.help')}
              />
            </TableCell>
          )}
        </>
      )}
    />
  )
}

export default BeginnerAllowedHoursRule
